import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import {
  BodyL,
  BodyM,
  BodyS,
  HeadlineXL,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { glossary } from '@sorare/core/src/lib/glossary';
import { getTextSize } from '@sorare/core/src/lib/text';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

const Number = styled.div`
  display: flex;
  align-items: flex-end;
  width: 100%;
  margin: auto;
`;

export const Body = styled(Vertical).attrs({ gap: 3 })`
  min-width: 380px;
  width: 100%;
  height: 100%;
  flex-grow: 1;
  padding: 0 var(--double-unit) var(--double-unit) var(--double-unit);
`;

export const ResponsiveNumber = ({
  total,
  className,
  alignmentBaseline = 'mathematical',
}: {
  total: string;
  className?: string;
  alignmentBaseline?: 'mathematical' | 'middle';
}) => {
  const { width, height } = getTextSize(
    `${total}`,
    `400 16px -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto',
    'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif`
  );
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className={className}>
      <text
        x="0"
        y={height / 2}
        fill="currentColor"
        alignmentBaseline={alignmentBaseline}
      >
        {total}
      </text>
    </svg>
  );
};

const CountRow = styled(Horizontal).attrs({ center: true, gap: 2 })`
  padding: var(--unit) 0;
  width: 100%;
  border-top: 1px solid var(--c-nd-200);
  border-bottom: 1px solid var(--c-nd-200);
`;

const Content = styled.div`
  width: 100%;
  min-height: 380px;
`;

const Footer = styled.div`
  width: 100%;
  padding: var(--double-unit);
  display: flex;
  justify-content: center;
`;

type RightBlockDialogProps = {
  title: ReactNode;
  subtitle: ReactNode;
  counts?: {
    label: ReactNode;
    count: string;
  }[];
  footer?: ReactNode;
  children: ReactNode;
  open: boolean;
  onClose: () => void;
};

export const Placeholder = styled(Horizontal).attrs({ gap: 0 })`
  width: 100%;
  justify-content: center;
  color: var(--c-nd-200);
`;

const RightBlockDialog = ({
  title,
  subtitle,
  counts,
  children,
  footer,
  ...rest
}: RightBlockDialogProps) => {
  return (
    <Dialog
      maxWidth="sm"
      footer={footer ? <Footer>{footer}</Footer> : undefined}
      {...rest}
    >
      <Body>
        <Vertical center>
          <HeadlineXL>{title}</HeadlineXL>
          <BodyM color="var(--c-nd-600)">{subtitle}</BodyM>
        </Vertical>
        {!!counts && counts.length > 0 && (
          <CountRow>
            {counts.map(({ label, count }) => (
              <Horizontal key={count}>
                <BodyS color="var(--c-nd-600)">{label}</BodyS>
                <BodyS>{count}</BodyS>
              </Horizontal>
            ))}
          </CountRow>
        )}
        <Content>{children}</Content>
      </Body>
    </Dialog>
  );
};

const StyledResponsiveNumber = styled(ResponsiveNumber)`
  width: 100%;
  max-height: 1em;
  @media ${desktopAndAbove} {
    max-height: 100px;
  }
`;

const Root = styled.button`
  height: 100%;
  display: flex;
  width: 100%;
  flex-direction: column;
  text-align: left;
  align-items: stretch;
`;

export const RightBlock = ({
  title,
  subTitle,
  total,
  badge,
  dialogProps,
  loading,
}: React.PropsWithChildren<{
  title: ReactNode;
  subTitle: ReactNode;
  total: string;
  dialogProps: Omit<RightBlockDialogProps, 'open' | 'onClose'>;
  loading?: boolean;
  badge?: number;
}>) => {
  const [open, setOpen] = useState(false);
  const isDesktop = useIsDesktopAndAbove();
  return (
    <>
      <Root type="button" onClick={() => setOpen(true)}>
        {isDesktop ? (
          <SBHorizontal>
            <div>
              <Horizontal>
                <Badge badgeContent={badge}>
                  <BodyL bold>{title}</BodyL>
                </Badge>
              </Horizontal>
              <BodyM>{subTitle}</BodyM>
            </div>
            <Button
              startIcon={<FontAwesomeIcon icon={faPlus} />}
              size="small"
              color="tertiary"
              onClick={() => setOpen(true)}
              disabled={loading}
              asSpan
            >
              <FormattedMessage {...glossary.more} />
            </Button>
          </SBHorizontal>
        ) : (
          <span>
            <Badge badgeContent={badge}>
              <LabelS color="var(--c-nd-600)">{title}</LabelS>
            </Badge>
          </span>
        )}
        <Number>
          <Skeleton loading={loading}>
            <div>
              <StyledResponsiveNumber total={total} />
            </div>
          </Skeleton>
        </Number>
      </Root>
      <RightBlockDialog
        open={open}
        onClose={() => setOpen(false)}
        {...dialogProps}
      />
    </>
  );
};
